import Script from 'next/script'
import { BaseSyntheticEvent, useState } from 'react'
import { smarty } from '../../smarty/Smarty'
import {
    AddressSuggestionListItem,
    AddressSuggestionsList,
} from '../../smarty/StyledComponents'
import { formattedAddressInList } from '../../smarty/utilities'
import { useUpdatePaymentState } from '../CheckoutPaymentContext'

// Forter Hosted Fields provides input fields for sensitive card data.
// https://docs.forter.com/docs/hosted-fields

// Smarty provides input fields/suggestions/autocomplete for the billing address.

// The card and address data is analyzed by Forter and processed by Fiserv before an order is placed.

export default function FiservPaymentFields() {
    const updateState = useUpdatePaymentState()
    const [addressSuggestions, setAddressSuggestions] = useState([])
    const [addressString, setAddressString] = useState('')
    const [debounceTimer, setDebounceTimer] = useState<any>()

    const iframeStyles = {
        input: {
            'font-size': '15px',
            color: '#30313d',
        },
        '.error': {
            color: 'red',
            'text-decorations': 'underline',
        },
    }

    const spanStyles = {
        fontSize: '15px',
        color: '#30313d',
    }

    const iframeWrapperStyles = {
        height: '42px',
        backgroundColor: '#ffffff',
        border: '1px solid #e6e6e6',
        padding: '12px',
        margin: '4px 0 12px',
        borderRadius: '4px',
        boxShadow:
            '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02)',
    }

    const initialize = async () => {
        const forterCollect = await window?.checkoutTools?.collect?.init({
            environment:
                process.env.NODE_ENV === 'production'
                    ? 'production'
                    : 'sandbox',
        })

        updateState({ forterCollect })

        await forterCollect.addFields({
            'card-number': {
                type: 'CARD_NUMBER',
                placeholder: '1234 1234 1234 1234',
                style: iframeStyles,
            },
            'card-expiration': {
                type: 'CARD_EXPIRATION_DATE',
                placeholder: 'MM / YY',
                style: iframeStyles,
            },
            'card-cvc': {
                type: 'CARD_CVC',
                placeholder: 'CVC',
                style: iframeStyles,
            },
            'card-holder-name': {
                type: 'CARD_HOLDER_NAME',
                placeholder: 'First and last name',
                style: iframeStyles,
            },
        })
    }

    const loadAddressSuggestions = (str: string) => {
        clearTimeout(debounceTimer)

        const timer = setTimeout(() => {
            smarty({ street: str }).then((suggestions) => {
                setAddressSuggestions(suggestions ?? [])
            })
        }, 300)

        setDebounceTimer(timer)
    }

    return (
        <>
            <div>
                <label>
                    <span style={spanStyles}>Card number</span>
                    <div id="card-number" style={iframeWrapperStyles}></div>
                </label>

                <div style={{ display: 'flex', gap: '12px' }}>
                    <label>
                        <span style={spanStyles}>Expiration</span>
                        <div
                            id="card-expiration"
                            style={iframeWrapperStyles}
                        ></div>
                    </label>

                    <label>
                        <span style={spanStyles}>CVC</span>
                        <div id="card-cvc" style={iframeWrapperStyles}></div>
                    </label>
                </div>

                <label>
                    <span style={spanStyles}>Full name</span>
                    <div
                        id="card-holder-name"
                        style={iframeWrapperStyles}
                    ></div>
                </label>

                <label style={{ position: 'relative' }}>
                    <span style={spanStyles}>Address</span>

                    <input
                        placeholder="Street address"
                        value={addressString}
                        onChange={(e: BaseSyntheticEvent) => {
                            setAddressString(e.target.value)
                            loadAddressSuggestions(e.target.value)
                        }}
                        style={{
                            display: 'block',
                            marginTop: '4px',
                            height: '42px',
                            borderRadius: '4px',
                            width: '100%',
                            fontSize: '15px',
                            padding: '12px',
                            verticalAlign: 'middle',
                            border: '1px solid #e6e6e6',
                            boxShadow:
                                '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02)',
                        }}
                    />

                    {!!addressSuggestions.length && (
                        <AddressSuggestionsList>
                            {addressSuggestions.map((address) => (
                                <AddressSuggestionListItem
                                    key={JSON.stringify(address)}
                                    onClick={() => {
                                        const {
                                            streetLine,
                                            secondary,
                                            city,
                                            state,
                                            zipcode,
                                        } = address

                                        const street = secondary
                                            ? `${streetLine} ${secondary}`
                                            : streetLine

                                        const formatted = `${street} ${city}, ${state}, ${zipcode}`

                                        setAddressString(formatted)
                                        updateState({ billingAddress: address })
                                        setAddressSuggestions([])
                                    }}
                                >
                                    {formattedAddressInList(address)}
                                </AddressSuggestionListItem>
                            ))}
                        </AddressSuggestionsList>
                    )}
                </label>
            </div>

            <Script
                type="text/javascript"
                id="checkoutTools__script"
                data-site-id={process.env.NEXT_PUBLIC_FORTER_API_SITE_ID}
                src="https://sdk.checkouttools.com/v1.2/collect.js"
                onLoad={() => initialize()}
            />
        </>
    )
}
